import { Controller } from "@hotwired/stimulus"
import maplibregl from "maplibre-gl"

const STATUS_FILTERS = {
  printed: ["==", ["get", "status"], "thanks_io_order_printed"],
  mailed: ["==", ["get", "status"], "thanks_io_order_shipped"],
  delivered: ["==", ["get", "status"], "thanks_io_order_delivered"]
}

export default class extends Controller {
  static targets = ["map", "filterBtn"]
  static values = {
    polygon: String,
    points: String
  }

  connect() {
    try {
      this.#setup()
    } catch (e) {
      console.error("recipient-map controller error:", e)
    }
  }

  disconnect() {
    this.map?.remove()
  }

  filter(event) {
    const filter = event.currentTarget.dataset.filter

    // Update button styles
    this.filterBtnTargets.forEach(btn => {
      if (btn.dataset.filter === filter) {
        btn.classList.add("btn-active")
        btn.classList.remove("btn-ghost")
      } else {
        btn.classList.remove("btn-active")
        btn.classList.add("btn-ghost")
      }
    })

    // Apply map filter
    if (!this.map) return

    const mapFilter = (filter === "all") ? null : STATUS_FILTERS[filter]

    if (!this.map.getLayer("recipient-dots")) return

    if (mapFilter) {
      this.map.setFilter("recipient-dots", mapFilter)
    } else {
      this.map.setFilter("recipient-dots", null)
    }
  }

  #setup() {
    const points = JSON.parse(this.pointsValue)

    // Always scope to polygon boundary on initial load
    let bounds
    if (this.polygonValue) {
      try {
        const polygon = JSON.parse(this.polygonValue)
        const coords = polygon.features
          ? polygon.features[0].geometry.coordinates[0]
          : polygon.coordinates[0]
        bounds = coords.reduce(
          (b, c) => b.extend(c),
          new maplibregl.LngLatBounds(coords[0], coords[0])
        )
      } catch (e) {
        // Fall back to points if polygon parsing fails
      }
    }

    // Fall back to points if no polygon bounds
    if (!bounds && points.features.length > 0) {
      bounds = points.features.reduce(
        (b, f) => b.extend(f.geometry.coordinates),
        new maplibregl.LngLatBounds(
          points.features[0].geometry.coordinates,
          points.features[0].geometry.coordinates
        )
      )
    }

    this.map = new maplibregl.Map({
      container: this.mapTarget,
      style: "https://tiles.openfreemap.org/styles/liberty",
      bounds: bounds,
      fitBoundsOptions: { padding: 40, animate: false },
      doubleClickZoom: false
    })

    this.map.on("error", (e) => console.error("MapLibre error:", e.error))
    this.map.on("load", () => {
      this.mapTarget.querySelector(".maplibregl-ctrl-attrib")?.classList.add("maplibregl-compact")
      this.#initLayers(points)
    })
  }

  #initLayers(points) {
    // Add polygon overlay if available
    if (this.polygonValue) {
      try {
        const polygon = JSON.parse(this.polygonValue)
        this.map.addSource("polygon", { type: "geojson", data: polygon })
        this.map.addLayer({
          id: "polygon-fill",
          type: "fill",
          source: "polygon",
          paint: { "fill-color": "#6366f1", "fill-opacity": 0.06 }
        })
        this.map.addLayer({
          id: "polygon-outline",
          type: "line",
          source: "polygon",
          paint: { "line-color": "#6366f1", "line-width": 1.5, "line-opacity": 0.3 }
        })
      } catch (e) {
        // polygon data might not be valid GeoJSON — skip silently
      }
    }

    // Add recipient points — color-coded by delivery status, uniform size
    this.map.addSource("recipients", { type: "geojson", data: points })

    this.map.addLayer({
      id: "recipient-dots",
      type: "circle",
      source: "recipients",
      paint: {
        "circle-radius": 6,
        "circle-color": ["get", "color"],
        "circle-opacity": 0.85,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#ffffff"
      }
    })

    // Popup on hover
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 8
    })

    this.map.on("mouseenter", "recipient-dots", (e) => {
      this.map.getCanvas().style.cursor = "pointer"
      const props = e.features[0].properties
      const statusMap = {
        thanks_io_order_printed: "Printed",
        thanks_io_order_shipped: "Mailed",
        thanks_io_order_delivered: "Delivered",
        unknown: "Ordered"
      }
      const status = statusMap[props.status] || "Ordered"
      const html = `<div class="text-xs"><strong>${props.address || "Unknown"}</strong><br>Status: ${status}</div>`
      popup.setLngLat(e.features[0].geometry.coordinates).setHTML(html).addTo(this.map)
    })

    this.map.on("mouseleave", "recipient-dots", () => {
      this.map.getCanvas().style.cursor = ""
      popup.remove()
    })
  }
}
